import { define } from '../../../utils/typeUtils'
import { DataType, TableName, TablesData } from '../../../tables'
import { AttributeMap, useDynamoActions, useDynamoActions2, DynamoApi, QueryOptions, TransactWriteOperation, TransactionCancelledErrorName } from '../../../services/dynamo'
import { RangeParams, Params } from '../../../state/reducers/types'
import { toCountResponse, toListResponse } from '../utils'
import { DbWriteOperation, ListResponse } from '../types'

// True when a transactWrite was rejected because one of its conditions failed, so nothing was written
export const isTransactionCancelledError = (error: unknown) =>
    error instanceof Error && error.name === TransactionCancelledErrorName

export const useDefaultDbActions = () => {
    const dynamoActions = useDynamoActions()
    const dynamoActions2 = useDynamoActions2()

    const getDbTableName = (tableName: TableName) => define(TablesData.get(tableName)).dbTableName

    return (dynamoApi: DynamoApi, companyId?: string) => {
        const getMultiple = async (
            tableName: TableName,
            options?: QueryOptions
        ) => {
            const { getMultiple } = dynamoActions2(dynamoApi, companyId)
            return getMultiple(getDbTableName(tableName), options)
                .then(response => toListResponse(response))
        }

        const count2 = async (
            tableName: TableName,
            options?: QueryOptions
        ) => {
            const { count } = dynamoActions2(dynamoApi, companyId)
            return count(getDbTableName(tableName), options)
                .then(response => toCountResponse(response))
        }

        // TODO analizar los de abajo de esta linea 1 por 1 si se puede reemplazar por getMultiple
        // los que no, implementar con mismo criterio que getMultiple
        
        const getAll = async (
            tableName: TableName,
            propName?: string
        ): Promise<DataType[]> => {
            const { getAll } = dynamoActions(dynamoApi, companyId)
            return getAll(getDbTableName(tableName), propName)
                .then(response => response as DataType[])
        }

        const getBatch = async (
            tableName: TableName,
            startKey?: Params,
            limit?: number,
            propName?: string
        ): Promise<ListResponse> => {
            const { getBatch } = dynamoActions(dynamoApi, companyId)
            return getBatch(getDbTableName(tableName), startKey, limit, propName)
                .then(toListResponse)
        }

        const get = async (
            tableName: TableName,
            id: string
        ): Promise<DataType> => {
            const { get } = dynamoActions(dynamoApi, companyId)
            return get(getDbTableName(tableName), id)
                .then(response => response as DataType)
        }

        const getByIds = async (
            tableName: TableName,
            ids: string[]
        ): Promise<DataType[]> => {
            const { getMultiple } = dynamoActions(dynamoApi, companyId)
            return getMultiple(getDbTableName(tableName), ids)
                .then(response => response as DataType[])
        }

        const getFiltered = async (
            tableName: TableName,
            paramsFilter?: Params,
            rangeFilters?: RangeParams,
            existingParamsFilter?: string[]
        ): Promise<DataType[]> => {
            const { getFiltered } = dynamoActions(dynamoApi, companyId)            
            return getFiltered(getDbTableName(tableName), paramsFilter, rangeFilters, existingParamsFilter)
                .then(response => response as DataType[])
        }

        const getFilteredByList = async (
            tableName: TableName,
            key: string,
            values: string[]
        ): Promise<DataType[]> => {
            const { getFilteredByList } = dynamoActions(dynamoApi, companyId)            
            return getFilteredByList(getDbTableName(tableName), key, values)
                .then(response => response as DataType[])    
        }

        const count = (
            tableName: TableName,
            startKey?: Params,
            limit?: number,
            paramsFilter?: Params
        ): Promise<number> => {
            const { count } = dynamoActions(dynamoApi, companyId)            
            return count(getDbTableName(tableName), startKey, limit, paramsFilter)
        }

        const save = async (
            tableName: TableName,
            dataTypes: DataType[]
        ): Promise<DataType[]> => {
            const { createOrUpdate } = dynamoActions(dynamoApi, companyId)            
            return createOrUpdate(getDbTableName(tableName), dataTypes as AttributeMap[])
                .then(unprocessedItems => unprocessedItems as DataType[])
        }

        const remove = async (
            tableName: TableName,
            ids: string[]
        ): Promise<void> => {
            const actions = dynamoActions(dynamoApi, companyId)
            return actions.remove(getDbTableName(tableName), ids)
                .then(_ => {})
        }

        const transactWrite = (operations: DbWriteOperation[]): Promise<void> => {
            const dbOperations = operations.map((operation): TransactWriteOperation => {
                const dbTableName = getDbTableName(operation.table)
                if (operation.type === 'put') {
                    return { type: 'put', dbTableName, item: operation.dataType as AttributeMap, expectedValues: operation.expectedValues }
                } else if (operation.type === 'delete') {
                    return { type: 'delete', dbTableName, id: operation.id, mustExist: operation.mustExist }
                } else {
                    return { type: 'increment', dbTableName, id: operation.id, increments: operation.increments, mustExist: operation.mustExist }
                }
            })
            return dynamoApi.transactWrite(dbOperations, define(companyId))
        }

        return {
            getMultiple,
            count2,

            getAll,
            getBatch,
            get,
            getByIds,
            getFiltered,
            getFilteredByList,
            count,
            save,
            remove,
            transactWrite
        }
    }
}
